import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  FEATURE_ALI_LOOKBACK,
  JURISDICTION_VIEW,
} from '@rsos/capstone/src/constants/capabilities';
import { useCurrentPSAPAccountID } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import { getALILocationHistory } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsAPI';
import { addLocation } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import isAgencyShareChatEnabled from '@rsos/utils/isAgencyShareChatEnabled';
// addLocToQueueSuccess is the JV / Agency-Share-Chat variant of addLocation,
// matching how live websocket-server locations are dispatched in StreamWS.js.
import { addLocToQueueSuccess } from '../../../actions';
import { useAppDispatch } from '../../../app';
import type { RootState } from '../../../app';

// Wait this long after a successful reconnect before backfilling, and confirm
// the socket is still connected, so a flapping connection doesn't fire a burst
// of lookbacks.
const RECONNECT_SETTLE_MS = 5_000;
// While reconnect attempts keep failing, perform at most one lookback per this
// interval.
const FALLBACK_THROTTLE_MS = 30_000;
// Look back slightly before the disconnect to avoid missing a record that
// landed in the same moment the connection dropped.
const DISCONNECT_LOOKBACK_BUFFER_MS = 30_000;
// Always look back at least this far. The websocket-server can stop delivering
// data before the disconnect is detected (e.g. during a failover), so the
// disconnect timestamp alone can under-count the gap. This is also the window
// within which a recent prior lookback is considered to already cover the data,
// so the reconnect lookback is skipped.
const MIN_LOOKBACK_WINDOW_MS = 5 * 60 * 1_000;
// The history endpoint rejects a `calls_after` older than 30 minutes, so the
// window is clamped to this regardless of how long the outage has lasted.
const MAX_LOOKBACK_WINDOW_MS = 30 * 60 * 1_000;

interface AliLocationRecord {
  caller_id?: string;
  [key: string]: unknown;
}

// The shared global typing for `window.__RSOS.streamWS.instance` (declared in
// shared/debug-panel) does not include the close-callback / isConnected surface
// this hook relies on. Type the instance locally rather than widening that
// shared declaration, which would fan out builds/approvals across every app
// that depends on debug-panel.
interface AliLookbackStreamWS {
  addOnMessageCallback: (callbacks: {
    [key: string]: (message: Record<string, unknown>) => void;
  }) => void;
  removeOnMessageCallback: (messageName: string) => void;
  addOnReconnectCallback: (callbacks: { [key: string]: () => void }) => void;
  removeOnReconnectCallback: (callbackName: string) => void;
  addOnCloseCallback: (callbacks: {
    [key: string]: (info: { reconnectionCount: number }) => void;
  }) => void;
  removeOnCloseCallback: (callbackName: string) => void;
  isConnected: () => boolean;
}

/**
 * Backfills ALI location records that may have been missed while the
 * websocket-server connection was down. On disconnect the disconnect time is
 * recorded; when the connection comes back (or while reconnect attempts keep
 * failing) the recent ALI history is fetched and the records are dispatched as
 * if they had arrived over the websocket-server connection.
 *
 * Gated behind the `feature_ali_lookback_0` authority capability and ANI/ALI
 * access. Registers on the shared StreamWS instance, mirroring the other
 * streamWS_hooks.
 */
const useHandleAliLookback = (): void => {
  const StreamWS = window?.__RSOS?.streamWS?.instance as
    | AliLookbackStreamWS
    | undefined;
  const dispatch = useAppDispatch();

  const isLookbackEnabled = useSelector(
    (state: RootState) =>
      !!state.psaps?.currentPSAP?.activeCapabilities?.[FEATURE_ALI_LOOKBACK],
  );
  const hasAniAliAccess = useSelector(checkAniAliAccessSelector);
  const accountId = useCurrentPSAPAccountID();
  const orgName = useSelector(
    (state: RootState) => state.sinatra?.user?.currentOrg?.name,
  );
  const isJVEnabled = useSelector(
    (state: RootState) =>
      !!state.psaps?.currentPSAP?.activeCapabilities?.[JURISDICTION_VIEW],
  );
  const isShareChatEnabled = useSelector((state: RootState) =>
    isAgencyShareChatEnabled(state.psaps),
  );

  const enabled = Boolean(isLookbackEnabled && hasAniAliAccess && accountId);

  // Mutable, render-independent state for the lookback lifecycle.
  const disconnectTimeRef = useRef<number | null>(null);
  const lastLookbackStartRef = useRef<number | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dispatchLocation = useCallback(
    (record: AliLocationRecord) => {
      if (!record?.caller_id) {
        return;
      }
      // Mirror StreamWS.handleLocations: JV / Agency-Share-Chat use
      // addLocToQueueSuccess, everything else uses addLocation.
      if (isJVEnabled || isShareChatEnabled) {
        dispatch(addLocToQueueSuccess(record));
      } else {
        dispatch(addLocation(record));
      }
    },
    [dispatch, isJVEnabled, isShareChatEnabled],
  );

  // Fires a single lookback request and dispatches the returned records.
  // Records the request start time only on success and resolves to whether the
  // request succeeded so callers can decide whether to retry. Throws nothing.
  const attemptLookback = useCallback(async (): Promise<boolean> => {
    const disconnectTime = disconnectTimeRef.current;
    if (disconnectTime == null || !accountId || !orgName) {
      return false;
    }

    const now = Date.now();
    // Look back at least MIN_LOOKBACK_WINDOW_MS, reach further back to cover a
    // longer outage (disconnect time minus a small buffer), and never exceed
    // the endpoint's 30-minute limit.
    const callsAfter = Math.max(
      now - MAX_LOOKBACK_WINDOW_MS,
      Math.min(
        now - MIN_LOOKBACK_WINDOW_MS,
        disconnectTime - DISCONNECT_LOOKBACK_BUFFER_MS,
      ),
    );

    try {
      const { data } = await getALILocationHistory({
        accountId,
        callsAfter,
        orgName,
      });
      // Record the time the (successful) request was started.
      lastLookbackStartRef.current = now;
      // The endpoint wraps the records in an envelope:
      // { account_id, calls_after, count, locations: [...] }.
      const records: AliLocationRecord[] = Array.isArray(data?.locations)
        ? data.locations
        : [];
      records.forEach(dispatchLocation);
      return true;
    } catch {
      return false;
    }
  }, [accountId, orgName, dispatchLocation]);

  // Reconnect succeeded: wait for the connection to settle, confirm it is still
  // connected, then backfill. Retry once on failure.
  const handleReconnect = useCallback(() => {
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
    }
    settleTimerRef.current = setTimeout(async () => {
      settleTimerRef.current = null;
      if (!StreamWS?.isConnected()) {
        return;
      }
      // Skip the reconnect lookback if one already ran within the minimum
      // window — that request already covers the recent data.
      const lastStart = lastLookbackStartRef.current;
      if (
        lastStart != null &&
        Date.now() - lastStart < MIN_LOOKBACK_WINDOW_MS
      ) {
        disconnectTimeRef.current = null;
        return;
      }
      const succeeded = await attemptLookback();
      if (!succeeded) {
        await attemptLookback();
      }
      disconnectTimeRef.current = null;
    }, RECONNECT_SETTLE_MS);
  }, [StreamWS, attemptLookback]);

  // Unintended close. reconnectionCount === 0 is the initial disconnect; a
  // value > 0 means a reconnect attempt failed to reopen the socket.
  const handleClose = useCallback(
    ({ reconnectionCount }: { reconnectionCount: number }) => {
      if (reconnectionCount === 0) {
        disconnectTimeRef.current = Date.now();
        return;
      }
      // Reconnect attempt failed: backfill if the last lookback was over the
      // throttle window ago (or none has run yet).
      const lastStart = lastLookbackStartRef.current;
      if (lastStart == null || Date.now() - lastStart > FALLBACK_THROTTLE_MS) {
        void attemptLookback();
      }
    },
    [attemptLookback],
  );

  useEffect(() => {
    if (!StreamWS || !enabled) {
      return;
    }

    StreamWS.addOnCloseCallback({ handleAliLookbackClose: handleClose });
    StreamWS.addOnReconnectCallback({
      handleAliLookbackReconnect: handleReconnect,
    });

    return () => {
      StreamWS.removeOnCloseCallback('handleAliLookbackClose');
      StreamWS.removeOnReconnectCallback('handleAliLookbackReconnect');
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
    };
  }, [StreamWS, enabled, handleClose, handleReconnect]);
};

export default useHandleAliLookback;
